import {
    createEntityAdapter, createSelector, createSlice,
} from '@reduxjs/toolkit';
import {omit, union} from 'lodash-es';

import {ICustomSelectEntry} from 'components/form/inputs/custom-select';
import {EDdParameterRuleItemSourceType} from 'components/report-configuration/tabs/report-dd-parameters/dd-parameters-rule/dd-parameters-rule-item/dd-parameters-rule-item-types';
import {TDDFilterExpression, TPredicateExpressionGroupTuple} from 'features/predicate-expression-form-list';
import {TAppState} from 'store/config/types';

import {createDdFiltersConfigurationExtraReducer, createDdFiltersConfigurationReducer} from './dd-filters-configuration/dd-filters-configuration-reducers';
import {createDdLookupsExtraReducer} from './dd-lookups';
import {createDdParametersExtraReducer} from './dd-parameters-configuration/dd-parameters-configuration-reducers';
import {IReportDdParameter} from './dd-parameters-configuration/dd-parameters-configuration-types';
import {
    createDdReferenceRulesExtraReducer,
    createDdReferenceRulesReducer,
    IReportDdReferenceRule,
} from './dd-references-configuration';
import {
    createDdRestrictionsConfigurationExtraReducer,
    createDdRestrictionsConfigurationReducer,
} from './dd-restrictions-configuration';
import {
    DD_REFERENCES_TABLE_DEFAULT_PAGINATION,
    REPORT_CONFIGURATION_SLICE,
} from './report-configuration-constants';
import {IReportConfigurationSliceState} from './report-configuration-types';
import {
    createTableReportConfigurationExtraReducer,
    createTableReportConfigurationExtraReducerMatchers,
} from './report-configuration/report-configuration-reducers';
import {
    createTableReportDepenceniesExtraReducer,
    createTableReportDependenciesReducer,
} from './report-dependencies';

export const ddReferenceRulesAdapter = createEntityAdapter<IReportDdReferenceRule>();
export const ddParametersAdapter = createEntityAdapter<IReportDdParameter>();

const reportConfigurationInitialState: IReportConfigurationSliceState = {
    reportConfigurationData: undefined,

    reportDependencies: {
        headerAttributes: undefined,
        tableAttributes: undefined,
        templateBlocks: undefined,
    },

    drilldownSettings: {
        ddReferenceRules: ddReferenceRulesAdapter.getInitialState([]),
        ddReferenceRulesTotalCount: undefined,
        ddReferenceRulesFilter: {
            paginationCurrent: DD_REFERENCES_TABLE_DEFAULT_PAGINATION.page,
            paginationPageSize: DD_REFERENCES_TABLE_DEFAULT_PAGINATION.pageSize,
        },

        ddParameters: ddParametersAdapter.getInitialState([]),
        ddChildReportQueryParameters: undefined,

        ddFilter: undefined,

        ddRestrictions: undefined,
    },
    ddLookups: {
        tableColumns: {},
        filters: undefined,
    },
};

export const selectDdParametersState = (
    state: IReportConfigurationSliceState,
) => state.drilldownSettings.ddParameters;

export const selectDdReferenceRulesState = (
    state: IReportConfigurationSliceState,
) => state.drilldownSettings.ddReferenceRules;

export const reportConfigurationSlice = createSlice({
    name: REPORT_CONFIGURATION_SLICE,
    initialState: reportConfigurationInitialState as IReportConfigurationSliceState,
    extraReducers: builder => {
        createDdReferenceRulesExtraReducer(builder);
        createDdParametersExtraReducer(builder);
        createDdLookupsExtraReducer(builder);
        createTableReportConfigurationExtraReducer(builder);
        createTableReportDepenceniesExtraReducer(builder);
        createDdFiltersConfigurationExtraReducer(builder);
        createDdRestrictionsConfigurationExtraReducer(builder);
        // -- matchers
        createTableReportConfigurationExtraReducerMatchers(builder);
    },
    reducers: {
        ...createDdReferenceRulesReducer(),
        ...createTableReportDependenciesReducer(),
        ...createDdFiltersConfigurationReducer(),
        ...createDdRestrictionsConfigurationReducer(),

        resetReportConfigurationData(state) {
            state.reportConfigurationData = undefined;
        },
    },
});

export const selectReportConfigurationSliceState = (state: TAppState) => state[REPORT_CONFIGURATION_SLICE];
export const selectTableReportConfigurationData = (state: TAppState) => selectReportConfigurationSliceState(
    state,
).reportConfigurationData;
export const selectTableReportConfigurationDependencies = (state: TAppState) => selectReportConfigurationSliceState(
    state,
).reportDependencies;
export const selectTableReportDrilldownSettings = (state: TAppState) => selectReportConfigurationSliceState(
    state,
).drilldownSettings;

export const selectDDChildReportQueryParameters = createSelector(selectTableReportDrilldownSettings,
    ddSettings => ddSettings.ddChildReportQueryParameters);

export const selectDrilldownFilterExpression = createSelector(
    selectTableReportDrilldownSettings,
    ddSettings => ddSettings.ddFilter as TDDFilterExpression,
);

export const selectDrilldownRestrictionsExpression = createSelector(
    selectTableReportDrilldownSettings,
    ddSettings => ddSettings.ddRestrictions as TPredicateExpressionGroupTuple,
);

export const selectUsedVariablesInDdFilterExpression = createSelector(
    selectTableReportDrilldownSettings,
    ddSettings => {
        const filter = ddSettings.ddFilter as TDDFilterExpression;
        if (!filter) return undefined;

        const variables: unknown[] = [];

        const getVariables = (exp: TDDFilterExpression) => {
            const [, ...operands] = exp;
            operands.forEach(oper => {
                if (Array.isArray(oper)) {
                    getVariables(oper);
                } else if (oper.parametrized && oper.enabled) variables.push(oper.comparisonValue);
            });
        };

        getVariables(filter);

        return union(variables);
    },
);

export const selectReportConfigurationHeaderAttributes = createSelector(
    selectTableReportConfigurationDependencies,
    ({headerAttributes}) => headerAttributes,
);

export const selectReportConfigurationTemplateBlocksCount = createSelector(
    selectTableReportConfigurationDependencies,
    ({templateBlocks}) => templateBlocks,
);

export const selectReportConfigurationHeaderAttributesAsSelectEntries = createSelector(
    [selectReportConfigurationHeaderAttributes, selectTableReportConfigurationData],
    headerAttributes => (!headerAttributes ? undefined : headerAttributes.map(({name, type, typeGroup}) => ({
        label: name,
        value: name,
        type: type ?? 'Тип не найден',
        typeGroup,
        name,
    }))),
);

export const selectReportConfigurationTableAttributes = createSelector(
    selectTableReportConfigurationDependencies,
    ({tableAttributes}) => tableAttributes,
);

export const selectReportConfigurationTableAttributesAsTypesRecords = createSelector(
    selectTableReportConfigurationDependencies,
    ({tableAttributes}) => (!tableAttributes ? tableAttributes
        : Object.fromEntries(tableAttributes?.map(attr => ([attr.name, attr.type])))),
);

export const selectReportConfigurationTableAttributesAsSelectEntries = createSelector(
    [(
        _, isFilteredByHidden?: boolean,
    ) => isFilteredByHidden,
    selectReportConfigurationTableAttributes,
    selectTableReportConfigurationData],
    (isFilteredByHidden, tableAttributes, configurationData) => {
        const {reportColumns} = configurationData ?? {};

        return !tableAttributes ? undefined : tableAttributes
            .filter(({name}) => {
                if (!isFilteredByHidden) return true;
                const hidden = reportColumns?.find(column => column?.keyName === name)?.hidden;
                return !hidden;
            })
            .map(({
                name,
                type,
                typeGroup,
                comment,
            }) => {
                const title = reportColumns?.find(column => column?.keyName === name)?.reportTitle;
                return {
                    label: title ?? name,
                    value: name,
                    type: type ?? 'Тип не найден',
                    typeGroup,
                    name,
                    comment,
                };
            });
    },
);

export const selectReportConfigurationMenuAsList = createSelector(
    selectTableReportConfigurationData,
    reportConfigurationData => {
        const reportMenu = reportConfigurationData?.reportMenu;
        const list: typeof reportMenu = [];
        const collectData = (menu: typeof reportMenu) => {
            if (!menu) return;
            menu.forEach(item => {
                const {children} = item ?? {};
                list.push(omit(item, ['children']));
                if (children) collectData(children);
            });
        };
        collectData(reportMenu);
        return list;
    },
);

export const selectReportConfigurationMenuAsListFilteredByReportColumns = createSelector(
    selectReportConfigurationMenuAsList, list => list.filter(item => !!item?.reportColumns),
);

export const selectReportConfigurationMenuReportAndCustomColumnsBySheetCode = createSelector([
    selectReportConfigurationMenuAsListFilteredByReportColumns,
    (_, sheetCode?: string) => sheetCode,
], (menuItems, sheetCode) => {
    if (!sheetCode) return undefined;
    const menuItem = menuItems.find(item => item?.sheetCode === sheetCode);
    return {
        reportColumns: menuItem?.reportColumns,
        customColumns: menuItem?.customColumns,
    };
});

const createSelectMenuAsEntriesList = (withReportColumnsFilter: boolean) => createSelector(
    withReportColumnsFilter
        ? selectReportConfigurationMenuAsListFilteredByReportColumns
        : selectReportConfigurationMenuAsList,
    list => list
        .filter(item => {
            const {sheetCode, name} = item ?? {};
            return !!sheetCode && !!name;
        })
        .map(item => {
            const {sheetCode, name} = item ?? {};
            return {
                label: name as string,
                value: sheetCode,
            };
        }) as ICustomSelectEntry<string, string>[],
);

export const selectReportConfigurationMenuAsEntriesList = createSelectMenuAsEntriesList(false);
export const selectReportConfigurationMenuAsEntriesListFilteredByReportColumns = createSelectMenuAsEntriesList(true);

export const selectDdParametersAsEntries = createSelector(selectTableReportDrilldownSettings, state => {
    const {ids, entities} = state.ddParameters;
    const parametersList = ids.map(id => entities[id]).filter(
        parameter => parameter?.childSourceType === EDdParameterRuleItemSourceType.filter && parameter.childValue,
    ).map(parameter => ({
        value: parameter!.childValue,
        label: parameter!.childValue,
    }));
    return parametersList;
});

export const ddParametersSelectors = {
    ...ddParametersAdapter.getSelectors<TAppState>(state => selectTableReportDrilldownSettings(state).ddParameters),
};

export const ddReferenceRulesSelectors = {
    ...ddReferenceRulesAdapter.getSelectors<TAppState>(
        state => selectTableReportDrilldownSettings(state).ddReferenceRules,
    ),
    selectDdReferenceRulesFilter: createSelector(selectTableReportDrilldownSettings,
        settings => settings.ddReferenceRulesFilter),
    selectDdReferenceRulesTotalCount: createSelector(selectTableReportDrilldownSettings,
        settings => settings.ddReferenceRulesTotalCount),
};

export const selectReportConfigurationMenuExcelTableHeaderStructureBySheetCode = createSelector([
    selectReportConfigurationMenuAsListFilteredByReportColumns,
    (_, sheetCode?: string) => sheetCode,
], (menuItems, sheetCode) => {
    if (!sheetCode) return undefined;
    const menuItem = menuItems.find(item => item?.sheetCode === sheetCode);
    return menuItem?.excelTableHeaderStructure;
});

export const {
    reducer: reportConfigurationSliceReducer,
    actions: reportConfigurationActions,
} = reportConfigurationSlice;
